const HEART_RATE_SERVICE = 0x180d;
const HEART_RATE_MEASUREMENT = 0x2a37;

export function isHeartRateBluetoothSupported() {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.bluetooth?.requestDevice === 'function'
  );
}

/** Parse BLE Heart Rate Measurement characteristic value (GATT spec). */
export function parseHeartRateMeasurement(dataView) {
  if (!dataView || dataView.byteLength < 2) return null;
  const flags = dataView.getUint8(0);
  const isUint16 = (flags & 0x01) === 0x01;
  if (isUint16) {
    if (dataView.byteLength < 3) return null;
    return dataView.getUint16(1, true);
  }
  return dataView.getUint8(1);
}

/**
 * Web Bluetooth HR strap helper for the kiosk.
 * Connect must be called from a user gesture.
 */
export function createHeartRateMonitor({ onSample, onStatus } = {}) {
  let device = null;
  let characteristic = null;
  let status = 'disconnected';

  function setStatus(next) {
    status = next;
    if (typeof onStatus === 'function') onStatus(next);
  }

  function handleCharacteristicValueChanged(event) {
    const value = event?.target?.value;
    if (!value) return;
    const bpm = parseHeartRateMeasurement(value);
    if (bpm == null || !Number.isFinite(bpm)) return;
    if (typeof onSample === 'function') {
      onSample({
        bpm: Math.round(bpm),
        recordedAt: new Date().toISOString(),
      });
    }
  }

  function handleDisconnected() {
    if (characteristic) {
      try {
        characteristic.removeEventListener(
          'characteristicvaluechanged',
          handleCharacteristicValueChanged,
        );
      } catch {
        // ignore
      }
    }
    characteristic = null;
    setStatus('disconnected');
  }

  async function bindDevice(nextDevice) {
    await teardown({ keepStatus: true });

    device = nextDevice;
    device.addEventListener('gattserverdisconnected', handleDisconnected);

    const server = await device.gatt.connect();
    const service = await server.getPrimaryService(HEART_RATE_SERVICE);
    characteristic = await service.getCharacteristic(HEART_RATE_MEASUREMENT);
    characteristic.addEventListener(
      'characteristicvaluechanged',
      handleCharacteristicValueChanged,
    );
    await characteristic.startNotifications();
    setStatus('connected');
    return device;
  }

  async function connectPreferredDevice() {
    if (typeof navigator.bluetooth.getDevices !== 'function') return null;
    const permitted = await navigator.bluetooth.getDevices();
    for (const candidate of permitted) {
      try {
        return await bindDevice(candidate);
      } catch {
        // try next permitted device
      }
    }
    return null;
  }

  async function connect({ forcePicker = false } = {}) {
    if (!isHeartRateBluetoothSupported()) {
      setStatus('unsupported');
      throw new Error('Web Bluetooth is not available in this browser');
    }

    setStatus('connecting');
    try {
      if (!forcePicker) {
        const preferred = await connectPreferredDevice();
        if (preferred) return preferred;
      }

      const nextDevice = await navigator.bluetooth.requestDevice({
        filters: [{ services: [HEART_RATE_SERVICE] }],
        optionalServices: [HEART_RATE_SERVICE],
      });

      return await bindDevice(nextDevice);
    } catch (error) {
      await teardown({ keepStatus: true });
      const name = error?.name || '';
      if (name === 'NotFoundError') {
        setStatus('disconnected');
      } else {
        setStatus('error');
      }
      throw error;
    }
  }

  async function teardown({ keepStatus = false } = {}) {
    if (characteristic) {
      try {
        characteristic.removeEventListener(
          'characteristicvaluechanged',
          handleCharacteristicValueChanged,
        );
        if (characteristic.properties?.notify) {
          await characteristic.stopNotifications();
        }
      } catch {
        // ignore stop/teardown errors
      }
      characteristic = null;
    }

    if (device) {
      try {
        device.removeEventListener('gattserverdisconnected', handleDisconnected);
      } catch {
        // ignore
      }
      try {
        if (device.gatt?.connected) device.gatt.disconnect();
      } catch {
        // ignore
      }
      device = null;
    }

    if (!keepStatus && status !== 'unsupported') setStatus('disconnected');
  }

  async function disconnect() {
    await teardown({ keepStatus: false });
  }

  function getStatus() {
    return status;
  }

  return {
    connect,
    disconnect,
    getStatus,
  };
}
